import cx from 'classnames';
import { extent } from 'd3-array';
import { scaleTime } from 'd3-scale';
import { select } from 'd3-selection';
import {
  area as d3Area,
  stack as d3Stack,
  stackOrderDescending,
} from 'd3-shape';
import { timeParse } from 'd3-time-format';
import { apmonth, intcomma } from 'journalize';
import 'd3-jetpack/essentials';

import createBase from './utils/d3-base';
import debounce from './utils/debounce';
import loadJsonScript from './utils/load-json-script';
import prepareData from './utils/prepare-data';

// ISO Dates
const dateParse = timeParse('%Y-%m-%d');

const trumpBeginDate = dateParse('2017-01-20');
const sessionsBeginDate = dateParse('2018-04-06');
const zeroEndDate = dateParse('2018-06-20');

// load the data inserted with `loadScript` in the template
const RAW_DATA = loadJsonScript('companies');

RAW_DATA.forEach(d => (d.date = dateParse(d.date)));
// `prepareData` gets everything organized for charting
const PREPARED_DATA = prepareData(RAW_DATA, 'date', [
  'BCFS Health and Human Services',
  'Tornillo (BCFS)',
]);

const stack = d3Stack().keys(PREPARED_DATA.columnNames);
const DATA = stack(RAW_DATA);

//console.log(DATA);
// reference to the container element
const container = select('#bcfs-chart');
// helper function for quickly clearing the container
const clearContainer = () => container.html('');

function render() {
  clearContainer();

  const chart = createBase({
    container,
    height: 350, // hardcoded height
    margin: { left: 35, top: 50 },
    x: scaleTime(),
    yAxisGap: 0,
  });

  const isMobile = chart.width < 544;

  chart.x.range([0, chart.width]).domain(extent(PREPARED_DATA.rowNames));
  chart.y.domain([0, 8500]);

  const [minY, maxY] = chart.y.domain();

  chart.xAxis.ticks(isMobile ? 4 : 8).tickFormat((d, i) => {
    const month = apmonth(d);

    if (d.getMonth() === 0 || i === 0) {
      return `${month} '${d
        .getFullYear()
        .toString()
        .slice(2)}`;
    }

    return month;
  });
  chart.yAxis.ticks(4).tickSize(-chart.width);

  // const area = d3Area()
  //   .x(d => chart.x(d.rowName))
  //   .y0(chart.y(0))
  //   .y1(d => chart.y(d.value));

  const area = d3Area()
    .x(d => chart.x(d.data.date))
    .y0(d => chart.y(d[0]))
    .y1(d => chart.y(d[1]));

  chart.drawAxis();

  // grouping for all the layers
  const layers = chart.g.append('g').attr('class', 'layers');

  const layer = layers
    .selectAll('.layer')
    .data(DATA)
    .enter()
    .append('g')
    .attr('class', 'layer');

  layer
    .append('path')
    .attr('class', d =>
      cx('area', {
        'area--yellow': d.key === 'Southwest Key Programs Inc.',
        'area--light-blue': d.key === 'BCFS Health and Human Services',
        'area--blue': d.key === 'Tornillo (BCFS)',
      })
    )
    .attr('d', area);

  const annotationsLayer = chart.g.append('g').attr('class', 'annotations');

  annotationsLayer
    .append('line')
    .attr('class', 'annotation-line')
    .attr('stroke-dasharray', '1.5')
    .attr('x1', chart.x(trumpBeginDate))
    .attr('y1', chart.y(minY))
    .attr('x2', chart.x(trumpBeginDate))
    .attr('y2', chart.y(maxY));

  annotationsLayer
    .append('text')
    .attr('class', 'annotation-label')
    .attr(
      'transform',
      `translate(${chart.x(trumpBeginDate) - 5},${chart.y(maxY) + 18})`
    )
    .tspans(['Trump', 'inaugurated'], 14);

  annotationsLayer
    .append('line')
    .attr('class', 'annotation-line')
    .attr('stroke-dasharray', '1.5')
    .attr('x1', chart.x(sessionsBeginDate))
    .attr('y1', chart.y(minY))
    .attr('x2', chart.x(sessionsBeginDate))
    .attr('y2', chart.y(maxY));

  annotationsLayer
    .append('text')
    .attr('class', 'annotation-label')
    .attr(
      'transform',
      `translate(${chart.x(sessionsBeginDate) - 5},${chart.y(maxY) + 18})`
    )
    .tspans(['Zero-tolerance', 'policy announced', 'on April 6'], 14);

  annotationsLayer
    .append('line')
    .attr('class', 'annotation-line')
    .attr('stroke-dasharray', '1.5')
    .attr('x1', chart.x(zeroEndDate))
    .attr('y1', chart.y(minY))
    .attr('x2', chart.x(zeroEndDate))
    .attr('y2', -50);

  annotationsLayer
    .append('text')
    .attr('class', 'annotation-label')
    .attr('transform', `translate(${chart.x(zeroEndDate) - 5},${-50 + 18})`)
    .tspans(['The policy', 'ended June 20'], 14);

  const bcfsLabel = annotationsLayer.append('g').attr('class', 'annotation');

  bcfsLabel.attr(
    'transform',
    `translate(${chart.x(dateParse('2018-07-22'))},${chart.y(700)})`
  );

  bcfsLabel
    .append('text')
    .attr('class', 'annotation-text')
    .attr('text-anchor', 'middle')
    .text('BCFS');

  const tornilloLabel = annotationsLayer
    .append('g')
    .attr('class', 'annotation');

  tornilloLabel.attr(
    'transform',
    `translate(${chart.x(dateParse('2018-10-15'))},${chart.y(300)})`
  );

  tornilloLabel
    .append('text')
    .attr('class', 'annotation-text')
    .attr('text-anchor', 'middle')
    .text('Tornillo');
}

const debouncedRender = debounce(() => render(), 300);
window.addEventListener('resize', debouncedRender);
debouncedRender();



// WEBPACK FOOTER //
// ./bcfs-chart.js